import React, { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterSite?: string;
  canonicalUrl?: string;
  robotsContent?: string;
  structuredData?: object;
  lang?: string;
}

export function SEOHead({
  title = 'CoreMonit - AI-Powered Infrastructure Monitoring Platform',
  description = 'CoreMonit provides unified server, application, microservices, database, logs, and cloud monitoring with real-time AI anomaly detection and auto-remediation. Start your free trial today.',
  keywords = [
    'infrastructure monitoring',
    'AI monitoring',
    'server monitoring',
    'application monitoring',
    'microservices monitoring',
    'database monitoring',
    'log monitoring',
    'cloud monitoring',
    'anomaly detection',
    'auto-remediation',
    'DevOps',
    'SRE',
    'observability',
    'APM'
  ],
  ogTitle,
  ogDescription,
  ogImage = '/og-image.png',
  ogUrl = 'https://coremonit.com',
  twitterCard = 'summary_large_image',
  twitterSite = '@CoreMonit',
  canonicalUrl,
  robotsContent = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  structuredData,
  lang = 'en'
}: SEOHeadProps) {
  
  useEffect(() => {
    // Set document title
    document.title = title;

    // Set or update meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords.join(', '));
    updateMetaTag('robots', robotsContent);
    updateMetaTag('language', lang);
    updateMetaTag('author', 'CoreMonit Team');
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');

    // Open Graph meta tags
    updateMetaTag('og:title', ogTitle || title, true);
    updateMetaTag('og:description', ogDescription || description, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:url', ogUrl, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:site_name', 'CoreMonit', true);
    updateMetaTag('og:locale', 'en_US', true);

    // Twitter Card meta tags
    updateMetaTag('twitter:card', twitterCard);
    updateMetaTag('twitter:site', twitterSite);
    updateMetaTag('twitter:title', ogTitle || title);
    updateMetaTag('twitter:description', ogDescription || description);
    updateMetaTag('twitter:image', ogImage);

    // Additional meta tags for better SEO
    updateMetaTag('theme-color', '#0891b2'); // Cyan-500
    updateMetaTag('msapplication-TileColor', '#0891b2');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');
    updateMetaTag('format-detection', 'telephone=no');

    // Canonical URL
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    // Structured Data (JSON-LD)
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    // Set document language
    document.documentElement.lang = lang;

  }, [
    title,
    description,
    keywords,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    twitterCard,
    twitterSite,
    canonicalUrl,
    robotsContent,
    structuredData,
    lang
  ]);

  return null; // This component doesn't render anything
}

// Predefined SEO configurations for different pages
export const seoConfigs = {
  home: {
    title: 'CoreMonit - AI-Powered Infrastructure Monitoring Platform',
    description: 'Unified monitoring for servers, applications, microservices, databases, logs, and cloud infrastructure with real-time AI anomaly detection and auto-remediation.',
    keywords: ['infrastructure monitoring', 'AI monitoring', 'server monitoring', 'DevOps', 'observability'],
    canonicalUrl: 'https://coremonit.com',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      'name': 'CoreMonit',
      'applicationCategory': 'DeveloperApplication',
      'operatingSystem': 'Web',
      'description': 'AI-powered infrastructure monitoring platform',
      'url': 'https://coremonit.com',
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD'
      }
    }
  },
  features: {
    title: 'Features - CoreMonit Infrastructure Monitoring',
    description: 'Explore CoreMonit\'s comprehensive monitoring features including AI anomaly detection, auto-remediation, real-time dashboards, and unified observability.',
    canonicalUrl: 'https://coremonit.com/features'
  },
  pricing: {
    title: 'Pricing - CoreMonit Plans & Free Trial',
    description: 'Choose the right CoreMonit plan for your infrastructure monitoring needs. Start with our free tier or explore enterprise solutions.',
    canonicalUrl: 'https://coremonit.com/pricing'
  },
  docs: {
    title: 'Documentation - CoreMonit Setup & Integration Guide',
    description: 'Complete documentation for CoreMonit setup, integration, and configuration. Get started with monitoring your infrastructure in minutes.',
    canonicalUrl: 'https://coremonit.com/docs'
  },
  contact: {
    title: 'Contact CoreMonit - Support & Sales',
    description: 'Get in touch with CoreMonit team for support, sales inquiries, or partnership opportunities. We\'re here to help with your monitoring needs.',
    canonicalUrl: 'https://coremonit.com/contact'
  },
  signup: {
    title: 'Sign Up - Start Your Free CoreMonit Trial',
    description: 'Create your CoreMonit account and start monitoring your infrastructure today. Free 14-day trial, no credit card required.',
    canonicalUrl: 'https://coremonit.com/signup',
    robotsContent: 'noindex, nofollow' // Don't index signup pages
  },
  signin: {
    title: 'Sign In - CoreMonit Dashboard Access',
    description: 'Access your CoreMonit monitoring dashboard and manage your infrastructure monitoring settings.',
    canonicalUrl: 'https://coremonit.com/signin',
    robotsContent: 'noindex, nofollow' // Don't index signin pages
  },
  product: { title: 'Product - CoreMonit', description: 'Unified AI-powered monitoring for your stack.', canonicalUrl: 'https://coremonit.com/product' },
  about: { title: 'About Us - CoreMonit', description: 'Our mission and team.', canonicalUrl: 'https://coremonit.com/about' },
  careers: { title: 'Careers - CoreMonit', description: 'Join the CoreMonit team.', canonicalUrl: 'https://coremonit.com/careers' },
  'help-center': { title: 'Help Center - CoreMonit', description: 'Guides and FAQs.', canonicalUrl: 'https://coremonit.com/help-center' },
  status: { title: 'Status - CoreMonit', description: 'Service health and incidents.', canonicalUrl: 'https://coremonit.com/status' },
  security: { title: 'Security - CoreMonit', description: 'How we protect your data.', canonicalUrl: 'https://coremonit.com/security' },
  'api-reference': { title: 'API Reference - CoreMonit', description: 'REST API reference.', canonicalUrl: 'https://coremonit.com/api-reference' },
  'api-docs': { title: 'API Docs - CoreMonit', description: 'Developer API documentation.', canonicalUrl: 'https://coremonit.com/api-docs' },
  sdks: { title: 'SDKs - CoreMonit', description: 'Official and community SDKs.', canonicalUrl: 'https://coremonit.com/sdks' },
  'integrations-dev': { title: 'Integrations - CoreMonit', description: 'Connect your tools.', canonicalUrl: 'https://coremonit.com/integrations' },
  webhooks: { title: 'Webhooks - CoreMonit', description: 'Event webhooks for automation.', canonicalUrl: 'https://coremonit.com/webhooks' },
  'trust-center': { title: 'Trust Center - CoreMonit', description: 'Security and compliance overview.', canonicalUrl: 'https://coremonit.com/trust-center' },
  terms: { title: 'Terms of Service - CoreMonit', description: 'CoreMonit Terms of Service.', canonicalUrl: 'https://coremonit.com/terms' },
  privacy: { title: 'Privacy Policy - CoreMonit', description: 'CoreMonit Privacy Policy.', canonicalUrl: 'https://coremonit.com/privacy' },
  cookies: { title: 'Cookie Policy - CoreMonit', description: 'How we use cookies.', canonicalUrl: 'https://coremonit.com/cookies' },
  dpa: { title: 'Data Processing Agreement - CoreMonit', description: 'DPA for enterprise customers.', canonicalUrl: 'https://coremonit.com/dpa' },
  'enterprise-saas': { title: 'Enterprise SaaS Agreement - CoreMonit', description: 'Enterprise license terms.', canonicalUrl: 'https://coremonit.com/enterprise-saas' },
  sla: { title: 'SLA - CoreMonit', description: 'Service level agreement.', canonicalUrl: 'https://coremonit.com/sla' },
  'billing-terms': { title: 'Billing Terms - CoreMonit', description: 'Subscription and billing terms.', canonicalUrl: 'https://coremonit.com/billing-terms' },
  'security-addendum': { title: 'Security Addendum - CoreMonit', description: 'Security and compliance addendum.', canonicalUrl: 'https://coremonit.com/security-addendum' },
  blog: { title: 'Blog - CoreMonit', description: 'News and articles from CoreMonit.', canonicalUrl: 'https://coremonit.com/blog' },
};

// Organization structured data for the company
export const organizationStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': 'CoreMonit',
  'url': 'https://coremonit.com',
  'logo': 'https://coremonit.com/logo.png',
  'description': 'AI-powered infrastructure monitoring platform',
  'foundingDate': '2024',
  'address': {
    '@type': 'PostalAddress',
    'addressCountry': 'US'
  },
  'contactPoint': {
    '@type': 'ContactPoint',
    'telephone': '+1-555-MONITOR',
    'contactType': 'customer service',
    'email': 'support@coremonit.com'
  },
  'sameAs': [
    'https://twitter.com/CoreMonit',
    'https://linkedin.com/company/coremonit',
    'https://github.com/coremonit'
  ]
};